import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useLocale } from '@hooks/useLocale';
import { useTranslation } from 'next-i18next';
import { PlayIcon } from '@phosphor-icons/react';
import cn from 'classnames';
import YouTubeEmbed, {
  YouTubePlayer,
} from '@components/cms/Slices/MediaEmbed/YouTubeEmbed';
import type { VideoEvent } from '@utils/gtm/ga4/trackVideoEvents';

import styles from './VideoBlock.module.scss';

const VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

const IFRAME_ALLOW =
  'autoplay; encrypted-media; picture-in-picture; fullscreen';

const escapeHtmlAttr = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const buildIframeHtml = (
  videoId: string,
  locale: string,
  title: string
): string => {
  if (!VIDEO_ID_PATTERN.test(videoId)) return '';
  const params = new URLSearchParams({
    rel: '0',
    playsinline: '1',
    controls: '1',
    iv_load_policy: '3',
    hl: locale,
    enablejsapi: '1',
    origin: window.location.origin,
  });

  const src = `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
  return `<iframe width="100%" height="100%" src="${src}" title="${escapeHtmlAttr(
    title
  )}" frameborder="0" allow="${IFRAME_ALLOW}" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`;
};

interface VideoBlockProps {
  videoId: string;
  videoTitle: string;
  interactionLocation: VideoEvent['interactionLocation'];
  posterImageUrl?: string;
  className?: string;
}

const VideoBlock = ({
  videoId,
  videoTitle,
  interactionLocation,
  posterImageUrl,
  className,
}: VideoBlockProps) => {
  const { t } = useTranslation('pdp');
  const locale = useLocale() === 'fr' ? 'fr' : 'en';

  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const playIntentRef = useRef(false);
  const previousVideoIdRef = useRef(videoId);

  const [isPlaying, setIsPlaying] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (previousVideoIdRef.current === videoId) return;
    previousVideoIdRef.current = videoId;
    setIsPlaying(false);
    setShouldLoad(false);
    playerRef.current = null;
    playIntentRef.current = false;
  }, [videoId]);

  // Pre-mount iframe before the user clicks so player.playVideo() can run
  // inside the click gesture — mobile browsers only allow audio when the play
  // call is synchronous within a user gesture.
  useEffect(() => {
    const node = containerRef.current;
    if (
      !node ||
      shouldLoad ||
      typeof window === 'undefined' ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldLoad]);

  const iframeHtml = shouldLoad
    ? buildIframeHtml(videoId, locale, videoTitle)
    : '';

  const handlePlayerReady = (player: YouTubePlayer) => {
    playerRef.current = player;
    if (playIntentRef.current) {
      playIntentRef.current = false;
      try {
        player.unMute();
        player.playVideo();
      } catch {
        // YT API can throw if the iframe was removed mid-init; safe to ignore.
      }
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    if (playerRef.current) {
      try {
        playerRef.current.unMute();
        playerRef.current.playVideo();
      } catch {
        // Player exists but rejected the call; fall back to autoplay-on-ready.
        playIntentRef.current = true;
      }
    } else {
      // User clicked before the iframe loaded. Force-mount and queue play —
      // mobile may fall back to muted because the gesture chain is broken.
      playIntentRef.current = true;
      setShouldLoad(true);
    }
  };

  if (!videoId || !VIDEO_ID_PATTERN.test(videoId)) {
    return null;
  }

  const posterSrc =
    posterImageUrl ?? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  return (
    <div
      ref={containerRef}
      className={cn(styles.container, className)}
      aria-label={videoTitle}
    >
      <div className={styles.frame}>
        {shouldLoad && (
          <div className={styles.iframeWrapper}>
            <YouTubeEmbed
              key={`${videoId}-${locale}`}
              html={iframeHtml}
              type="PDP"
              interactionLocation={interactionLocation}
              onPlayerReady={handlePlayerReady}
              className={styles.embedFill}
            />
          </div>
        )}
        {!isPlaying && (
          <button
            type="button"
            className={styles.poster}
            onClick={handlePlay}
            aria-label={t('video.play.aria', { title: videoTitle })}
          >
            <Image
              className={styles.posterImage}
              src={posterSrc}
              alt=""
              fill
              objectFit="cover"
              loading="lazy"
              decoding="async"
              onError={
                posterSrc === posterImageUrl
                  ? undefined
                  : (e) => {
                      // Fallback to custom poster if YouTube thumbnail fails to load
                      if (posterImageUrl) {
                        (e.currentTarget as HTMLImageElement).src =
                          posterImageUrl;
                      }
                    }
              }
            />
            <span className={styles.playButton} aria-hidden="true">
              <PlayIcon size={24} weight="fill" />
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoBlock;
